import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useQuery } from '@tanstack/react-query';
import { AppSettings } from '../types';
import { ShieldAlert, ArrowRight, Database } from 'lucide-react';
import { GoogleButton } from '../features/auth/GoogleButton';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: settings } = useQuery({
    queryKey: ['appSettingsPublic'],
    queryFn: async () => {
      const { data } = await supabase.from('app_settings').select('*').single();
      return data as AppSettings;
    }
  });

  const branding = settings?.branding;

  useEffect(() => {
    if (branding?.logo_url) {
      // @ts-ignore
      window.updateFavicon(branding.logo_url);
    }
    if (branding?.name) {
      document.title = branding.name;
    }
  }, [branding]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { error: authErr } = await supabase.auth.signInWithPassword({ email, password });
      if (authErr) throw authErr;
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9] px-4 py-12">
      <div className="max-w-6xl w-full bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col md:flex-row animate-in fade-in slide-in-from-bottom-8 duration-700">
        
        {/* Banner Side */}
        <div className="md:w-3/5 relative bg-slate-900 overflow-hidden min-h-[400px]">
          {branding?.banner_url ? (
            <img 
              src={branding.banner_url} 
              className="absolute inset-0 w-full h-full object-cover opacity-60" 
              alt="Background" 
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-black opacity-80" />
          )}
          <div className="absolute inset-0 bg-black/30" />
          
          <div className="relative h-full p-16 flex flex-col justify-between z-10">
            <div className="flex items-center gap-4">
              {branding?.logo_url ? (
                <img src={branding.logo_url} className="w-14 h-14 object-contain" alt="Logo" />
              ) : (
                <div className="w-14 h-14 bg-[#FFDB00] rounded-xl flex items-center justify-center text-black">
                   <Database size={32} />
                </div>
              )}
              <h1 className="text-white text-3xl font-black tracking-widest uppercase leading-none">
                {branding?.name || 'HFLI Data'}
              </h1>
            </div>

            <div className="space-y-6">
              <h2 className="text-5xl lg:text-6xl font-black text-white leading-tight uppercase tracking-tighter">
                {branding?.tagline || ''}
              </h2>
              {branding?.tagline && <div className="w-24 h-2 bg-[#FFDB00] rounded-full" />}
            </div>

            <div className="flex items-center gap-4">
               <div className="px-3 py-1 bg-white/10 rounded border border-white/20 text-[10px] font-black text-white uppercase tracking-widest">
                  Identity Portal
               </div>
            </div>
          </div>
        </div>

        {/* Identity Side */}
        <div className="md:w-2/5 p-12 lg:p-20 flex flex-col justify-center space-y-10 bg-white">
          <div className="space-y-2">
            <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tight">Identity Access</h3>
            <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.3em]">Authorized Personnel Only</p>
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-100 p-4 rounded-lg text-[10px] font-black text-rose-600 uppercase flex items-center gap-3">
              <ShieldAlert size={16} />
              {error}
            </div>
          )}

          <div className="space-y-8">
            <GoogleButton isLoading={loading} onLoading={setLoading} />

            {!settings?.force_google_auth && (
              <>
                <div className="relative flex items-center justify-center text-[9px] font-black uppercase tracking-[0.4em] text-slate-300">
                  <span className="px-6 bg-white z-10 italic">Internal Credentials</span>
                  <div className="absolute w-full border-t border-slate-100"></div>
                </div>

                <form className="space-y-6" onSubmit={handleEmailLogin}>
                  <Input label="Email Account" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address..." />
                  <Input label="Access Key" type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
                  <Button type="submit" isLoading={loading} className="w-full py-6 text-sm" icon={<ArrowRight size={20} />}>Initialize Session</Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;